import React, { useState } from 'react'
import { FormContext } from 'react-hook-form'
import { isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import { Box } from '@material-ui/core'

import {
  AcceptedForm,
  AcceptedSection,
  ApprovalForm,
  DocumentSettings,
  DownloadVersionDialog,
  InformationSection,
  VersionAlertDialog,
  VersionTable,
} from './components'
import { ControlsSection, SectionCard } from 'components'

import theme from 'theme'
import constants from 'constants/index'

const {
  ACCEPTED_DOCUMENTS_CATEGORY_ID,
  APPROVAL_DOCUMENTS_CATEGORY_ID,
  DOCUMENTS_CATEGORY_ID,
  HISTORY_DOCUMENTS_CATEGORY_ID,
  SETTINGS_DOCUMENTS_CATEGORY_ID,
  CONTROLS_DOCUMENTS_CATEGORY_ID,
} = constants.documents

const DocumentContent = ({
  type,
  currentTab,
  isLoading,
  documentData = {},
  form = {},
  pageActions = {},
  versionAlertDialog = {},
  settings = {},
  versions = {},
  controls = {},
}) => {
  const {
    id: documentId,
    current: currentDocument,
    consentForm,
    isWrittenContent,
  } = documentData

  const {
    methods: formMethods,
    actionButtons: formActionButtons,
    onSubmit,
    errorRef: settingsErrorRef,
    initialized: formInitialized,
  } = form

  const { handleBackRedirection, setLoading, refresh } = pageActions

  const { open: openVersionDialog, setOpen: setOpenVersionDialog } =
    versionAlertDialog

  const {
    availableUsers,
    departments,
    departmentsIsLoading,
    permissionsIsLoading,
    usersIsLoading,
  } = settings

  const {
    documentVersions,
    isLoading: documentVersionsIsLoading,
    downloadDocument,
    pagination: versionTablePagination,
  } = versions

  const [openDialog, setOpenDialog] = useState(null)

  const isEditorTab = currentTab === DOCUMENTS_CATEGORY_ID
  const isSettingsTab = currentTab === SETTINGS_DOCUMENTS_CATEGORY_ID
  const isAcceptedTab = currentTab === ACCEPTED_DOCUMENTS_CATEGORY_ID
  const isApprovalTab = currentTab === APPROVAL_DOCUMENTS_CATEGORY_ID
  const isHistoryTab = currentTab === HISTORY_DOCUMENTS_CATEGORY_ID
  const isControlTab = currentTab === CONTROLS_DOCUMENTS_CATEGORY_ID

  if (type === 'show') {
    return (
      <>
        {!isLoading && formInitialized && (
          <>
            <Box display={isEditorTab ? 'block' : 'none'}>
              <InformationSection
                renderMode="editor"
                type="show"
                currentDocument={currentDocument}
              />
            </Box>

            <Box display={isSettingsTab ? 'block' : 'none'}>
              <DocumentSettings type="show" currentDocument={currentDocument} />
            </Box>
          </>
        )}

        {isAcceptedTab && !isLoading && (
          <AcceptedSection
            consentFormId={currentDocument?.consentFormId}
            currentDocument={currentDocument}
          />
        )}

        {isApprovalTab && !isLoading && (
          <ApprovalForm
            currentDocument={currentDocument}
            documentId={documentId}
            handleBackRedirection={handleBackRedirection}
            setLoading={setLoading}
            type="show"
          />
        )}

        {isControlTab && isWrittenContent && !isLoading && (
          <ControlsSection
            documentId={documentId}
            controls={controls}
            readOnly
          />
        )}

        {isHistoryTab && isWrittenContent && !isLoading && (
          <SectionCard title="Histórico de versões" isTable hasPagination>
            <VersionTable
              documentVersions={documentVersions}
              isLoading={documentVersionsIsLoading}
              downloadDocument={downloadDocument}
              documentId={documentId}
              setOpenDialog={setOpenDialog}
            />
            {versionTablePagination}
            {!isEmpty(openDialog) && (
              <DownloadVersionDialog
                open={openDialog}
                setOpen={setOpenDialog}
                downloadDocument={downloadDocument}
              />
            )}
          </SectionCard>
        )}
      </>
    )
  }

  if (type === 'create') {
    if (isLoading) return null

    const { handleSubmit } = formMethods

    return (
      <FormContext {...formMethods}>
        <Box component="form" onSubmit={handleSubmit(() => {})}>
          <Box display={isEditorTab ? 'block' : 'none'}>
            <Box
              display="flex"
              flexDirection="column"
              gridGap={theme.spacing(4)}
            >
              <InformationSection renderMode="editor" type="create" />
              {formActionButtons}
            </Box>
          </Box>

          <Box display={isSettingsTab ? 'block' : 'none'}>
            <Box
              display="flex"
              flexDirection="column"
              gridGap={theme.spacing(4)}
            >
              <DocumentSettings
                type="create"
                availableUsers={availableUsers}
                departments={departments}
                permissionsIsLoading={permissionsIsLoading}
                usersIsLoading={usersIsLoading}
                departmentsIsLoading={departmentsIsLoading}
              />
              {formActionButtons}
              <div ref={settingsErrorRef} />
            </Box>
          </Box>
        </Box>
      </FormContext>
    )
  }

  const { handleSubmit } = formMethods

  return (
    <>
      {!isLoading && formInitialized && (
        <FormContext {...formMethods}>
          <Box
            component="form"
            id="document-edit-form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <Box display={isEditorTab ? 'block' : 'none'}>
              <Box
                display="flex"
                flexDirection="column"
                gridGap={theme.spacing(4)}
              >
                <InformationSection
                  renderMode="editor"
                  type="edit"
                  currentDocument={currentDocument}
                />
                {formActionButtons}
              </Box>
            </Box>

            <Box display={isSettingsTab ? 'block' : 'none'}>
              <Box
                display="flex"
                flexDirection="column"
                gridGap={theme.spacing(4)}
              >
                <DocumentSettings
                  type="edit"
                  currentDocument={currentDocument}
                  availableUsers={availableUsers}
                  departments={departments}
                  permissionsIsLoading={permissionsIsLoading}
                  usersIsLoading={usersIsLoading}
                  departmentsIsLoading={departmentsIsLoading}
                />
                {formActionButtons}
                <div ref={settingsErrorRef} />
              </Box>
            </Box>

            <VersionAlertDialog
              open={openVersionDialog}
              setOpen={setOpenVersionDialog}
              onSubmit={handleSubmit((data) => onSubmit(data))}
              collectApproval={currentDocument?.collectApproval}
              collectAccepted={currentDocument?.collectAccepted}
            />
          </Box>
        </FormContext>
      )}

      {isAcceptedTab && !isLoading && (
        <AcceptedForm
          currentDocument={currentDocument}
          consentForm={consentForm}
          documentId={documentId}
          handleBackRedirection={handleBackRedirection}
          refresh={refresh}
          setLoading={setLoading}
          type="edit"
        />
      )}

      {isApprovalTab && !isLoading && (
        <ApprovalForm
          currentDocument={currentDocument}
          documentId={documentId}
          handleBackRedirection={handleBackRedirection}
          refresh={refresh}
          setLoading={setLoading}
          type="edit"
        />
      )}

      {isControlTab && isWrittenContent && !isLoading && (
        <ControlsSection
          documentId={documentId}
          documentStatus={currentDocument?.status}
          controls={controls}
        />
      )}

      {isHistoryTab && isWrittenContent && !isLoading && (
        <SectionCard title="Histórico de versões" isTable hasPagination>
          <VersionTable
            documentVersions={documentVersions}
            isLoading={documentVersionsIsLoading}
            downloadDocument={downloadDocument}
            documentId={documentId}
            setOpenDialog={setOpenDialog}
          />
          {versionTablePagination}
          {!isEmpty(openDialog) && (
            <DownloadVersionDialog
              open={openDialog}
              setOpen={setOpenDialog}
              downloadDocument={downloadDocument}
            />
          )}
        </SectionCard>
      )}
    </>
  )
}

DocumentContent.propTypes = {
  type: PropTypes.oneOf(['show', 'create', 'edit']).isRequired,
  currentTab: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  documentData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    current: PropTypes.object,
    consentForm: PropTypes.object,
    isWrittenContent: PropTypes.bool,
  }),
  form: PropTypes.shape({
    methods: PropTypes.object,
    actionButtons: PropTypes.node,
    onSubmit: PropTypes.func,
    errorRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    initialized: PropTypes.bool,
  }),
  pageActions: PropTypes.shape({
    handleBackRedirection: PropTypes.func,
    setLoading: PropTypes.func,
    refresh: PropTypes.func,
  }),
  versionAlertDialog: PropTypes.shape({
    open: PropTypes.bool,
    setOpen: PropTypes.func,
  }),
  settings: PropTypes.shape({
    availableUsers: PropTypes.array,
    departments: PropTypes.array,
    departmentsIsLoading: PropTypes.bool,
    permissionsIsLoading: PropTypes.bool,
    usersIsLoading: PropTypes.bool,
  }),
  versions: PropTypes.shape({
    documentVersions: PropTypes.array,
    isLoading: PropTypes.bool,
    downloadDocument: PropTypes.func,
    pagination: PropTypes.node,
  }),
  controls: PropTypes.shape({
    linkedDocuments: PropTypes.array,
    isLoading: PropTypes.bool,
    pagination: PropTypes.node,
    refresh: PropTypes.func,
  }),
}

export default DocumentContent
